import { useContext } from "react";
import { useMutation, useQuery, useQueryClient, type QueryKey, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import toast from "react-hot-toast";
import api from "./api";
import AuthContext from "src/store/auth-context";
import { applyOptimisticPatches, type OptimisticPatch, type OptimisticSnapshot } from "./useApi.helpers";
import type { CustomAxiosError } from "@/interfaces/error-types";

export type UseApiQueryOptions<TRaw, TData = TRaw> = {
  url: string;
  queryKey: QueryKey;
  enabled?: boolean;
  transform?: (raw: TRaw) => TData;
  requiresAuth?: boolean; // default true
  /** Poll the endpoint on an interval (ms). Pass `false` (default) to disable background refetching. */
  refetchInterval?: number | false;
};

export function useApiQuery<TRaw, TData = TRaw>({ url, queryKey, enabled = true, transform, requiresAuth = true, refetchInterval = false }: UseApiQueryOptions<TRaw, TData>): UseQueryResult<TData> {
  const authCtx = useContext(AuthContext);
  return useQuery<TData>({
    queryKey,
    enabled: enabled && (requiresAuth ? !!authCtx.token : true),
    refetchOnWindowFocus: false,
    refetchInterval,
    queryFn: async () => {
      const headers = authCtx.token ? { Authorization: "Bearer " + authCtx.token } : undefined;
      const response = await api.get<TRaw>(url, headers ? { headers } : undefined);
      return transform ? transform(response.data) : (response.data as unknown as TData);
    },
  });
}

type WriteMethod = "POST" | "PUT" | "DELETE" | "PATCH";

export type UseApiMutationOptions<TData, TVars> = {
  method: WriteMethod;
  url: string | ((vars: TVars) => string);
  body?: (vars: TVars) => unknown;
  optimistic?: (vars: TVars) => OptimisticPatch[] | undefined;
  invalidate?: QueryKey[];
  onDone?: (data: TData, vars: TVars) => void;
  onFail?: (err: CustomAxiosError, vars: TVars) => void;
  successToast?: string | ((data: TData, vars: TVars) => string);
  errorToast?: string | ((err: CustomAxiosError, vars: TVars) => string);
};

export function useApiMutation<TData, TVars = void>({
  method,
  url,
  body,
  optimistic,
  invalidate,
  onDone,
  onFail,
  successToast,
  errorToast,
}: UseApiMutationOptions<TData, TVars>): UseMutationResult<TData, CustomAxiosError, TVars, OptimisticSnapshot | undefined> {
  const authCtx = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation<TData, CustomAxiosError, TVars, OptimisticSnapshot | undefined>({
    mutationFn: async (vars: TVars) => {
      const resolvedUrl = typeof url === "function" ? url(vars) : url;
      const payload = body ? body(vars) : vars;
      const headers = { Authorization: "Bearer " + authCtx.token };
      const lower = method.toLowerCase() as "post" | "put" | "delete" | "patch";
      if (lower === "delete") {
        const response = await api.delete<TData>(resolvedUrl, { headers, data: payload });
        return response.data;
      }
      const response = await api[lower]<TData>(resolvedUrl, payload, { headers });
      return response.data;
    },
    onMutate: (vars) => {
      const patches = optimistic?.(vars);
      if (patches && patches.length > 0) {
        return applyOptimisticPatches(queryClient, patches);
      }
      return undefined;
    },
    onError: (err, vars, snapshot) => {
      snapshot?.rollback();
      if (errorToast) {
        const msg = typeof errorToast === "function" ? errorToast(err, vars) : errorToast;
        toast.error(msg);
      }
      onFail?.(err, vars);
    },
    onSuccess: (data, vars) => {
      if (successToast) {
        const msg = typeof successToast === "function" ? successToast(data, vars) : successToast;
        toast.success(msg);
      }
      onDone?.(data, vars);
    },
    onSettled: () => {
      if (invalidate) {
        for (const key of invalidate) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });
}
